import {
  faArchive,
  faCheck,
  faClone,
  faCloudUpload,
  faCog,
  faDownload,
  faGlobe,
  faImage,
  faNewspaper,
  faPencil,
  faPlus,
  faPrint,
  faTimes,
  faTrashAlt,
} from '@fortawesome/pro-light-svg-icons'
import { faCheckCircle } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'

// @ts-expect-error TS7016
import { ArtworkDownload } from '@domain-group/artwork-builder'
import { digObject } from '@domain-group/javascript-utils'
import {
  Box, Button, FormField, IconWithMenu, Image, LoadingBubbles, Text,
} from '@domain-group/suit-theme'

import { getCampaignType } from '@functions/campaign'
import { confirmAction, confirmDelete } from '@functions/entity'
import handleCallbackAction from '@functions/handleCallbackAction'

import useArtwork from '@hooks/useArtwork'
import useRedirectUrlWithCacheKey from '@hooks/useRedirectUrlWithCacheKey'

import EnabledPermission from '@components/EnabledPermission'

import type { UseOrderItemPayload } from '@hooks/useOrderItem'

import Activity from './Activity'
import DataChanges from './DataChanges'
import Pages from './Pages'
import Reviews from './Reviews'

interface ArtworkProps {
  orderItemPayload: UseOrderItemPayload,
}

const Artwork = (props: ArtworkProps) => {
  const {
    orderItemPayload: {
      artwork,
      artworkApproved,
      artworkImageDownloadable,
      artworkImposable,
      artworkUnapprovable,
      callbacks: {
        createArtwork,
        showUploadArtworkModal,
      },
      canChangeArtworkStatus,
      canCreateArtwork,
      hasArtworkBackups,
      isPublicationOrder,
      orderItem,
      paginatedProofDownloadable,
      publicationBooking,
    },
  } = props

  const { redirectUrl } = useRedirectUrlWithCacheKey([orderItem.id])

  const artworkPayload = useArtwork(artwork, {
    generateUrlsOptions: {
      includeRedirect: true,
      redirectUrl,
    },
  })

  const {
    callbacks: {
      approveArtwork: approveFn,
      createArtworkFromBackup: createFromBackupFn,
      deleteArtwork: deleteFn,
      unapproveArtwork: unapproveFn,
    },
    campaign,
    creating,
    filteredArtworkPages,
    isUploadedArtwork,
    selectedArtworkPage,
    // @ts-expect-error TS2339
    updatingArtworkPreview,
    urls: {
      editArtworkUrl,
    },
  } = artworkPayload

  const artworkPreviewUrl = digObject(selectedArtworkPage, 'previews.preserved_560x560')
  const pageCount = filteredArtworkPages.length

  return (
    <>
      <FormField
        direction="column"
        label={pageCount <= 1 ? 'Artwork' : `Artwork (${pageCount} Pages)`}
        style={{ marginTop: 16 }}
      >
        <Box alignItems="center" height={343} justifyContent="center" variant="whiteGrey">
          {/* No Artwork - Create Artwork */}
          {/*
           // @ts-expect-error TS2339 */}
          {canCreateArtwork && !artwork.id && (
            <Box flexDirection="column" width="auto">
              <Button
                buttonStyle="primaryCreate"
                icon={<FontAwesomeIcon icon={faPlus} />}
                onClick={() => createArtwork(redirectUrl)}
                size="medium"
              >
                Create Artwork
              </Button>

              {hasArtworkBackups && (
                <Button
                  buttonStyle="secondaryUtility"
                  icon={<FontAwesomeIcon icon={faArchive} />}
                  loading={creating}
                  // @ts-expect-error TS2322
                  onClick={() => handleCallbackAction({
                    callbacks: {
                      action: () => createFromBackupFn({ orderItemId: orderItem.id }),
                    },
                    shouldRedirect: true,
                  })}
                  // @ts-expect-error TS2322
                  marginTop="medium"
                  size="medium"
                >
                  Restore Backup
                </Button>
              )}
            </Box>
          )}
          {/* End No Artwork - Create Artwork */}

          {/* Artwork Preview */}
          {updatingArtworkPreview && <LoadingBubbles color="#8c8c8e" />}

          {!updatingArtworkPreview && artworkPreviewUrl && (
            <Image backgroundSize="contain" height="100%" width="100%" url={artworkPreviewUrl} />
          )}

          {/* Custom Artwork - Extracting Pages */}
          {isUploadedArtwork && !artworkPreviewUrl && (
            <Text color="bodyFontLightColor">
              Refresh Page to View Preview
            </Text>
          )}
          {/* End Custom Artwork - Extracting Pages */}
        </Box>

        {/*
         // @ts-expect-error TS2339 */}
        {artwork.id && (
          <Box flexDirection="column" marginTop="medium">
            {/* Edit Artwork Button */}
            <Box alignItems="center">
              {artworkApproved && (
                <Box
                  alignItems="center"
                  backgroundColor="#F2FDF6"
                  border="1px solid"
                  borderColor="#C6E9D3"
                  borderRadius={5}
                  fontSize="small"
                  justifyContent="center"
                  padding="large"
                >
                  <Text color="green" marginRight="medium">
                    <FontAwesomeIcon icon={faCheckCircle} />
                  </Text>
                  <Text color="green">Artwork Approved</Text>
                </Box>
              )}

              {canCreateArtwork && !artworkApproved && (
                <Button
                  as={isUploadedArtwork ? undefined : 'a'}
                  buttonStyle="secondaryUtility"
                  href={isUploadedArtwork ? null : editArtworkUrl}
                  icon={<FontAwesomeIcon icon={isUploadedArtwork ? faCloudUpload : faPencil} />}
                  // @ts-expect-error TS2322
                  onClick={isUploadedArtwork ? showUploadArtworkModal : null}
                  size="medium"
                  marginRight="medium"
                  width="100%"
                >
                  {isUploadedArtwork ? 'Change Artwork' : 'Edit Artwork'}
                </Button>
              )}
            </Box>
            {/* End Edit Artwork Button */}

            {/* Download & More Options */}
            <Box alignItems="center" marginTop="medium">
              <IconWithMenu
                boxProps={{
                  flexGrow: 1,
                  width: '100%',
                }}
                icon={(
                  <Button
                    buttonStyle="secondaryUtility"
                    icon={<FontAwesomeIcon icon={faDownload} />}
                    size="medium"
                    // @ts-expect-error TS2322
                    width="100%"
                  >
                    Download
                  </Button>
                )}
              >
                {/*
                 // @ts-expect-error TS2741 */}
                <IconWithMenu.Menu
                  closeOnClick
                  listStyle={{ left: 0, right: 'auto' }}
                  nibStyle={{ left: 21, right: 'auto' }}
                >
                  <ArtworkDownload artwork={artwork} key="print">
                    {/*
                      // @ts-expect-error TS2339 */}
                    <a href={artwork.links?.download_url}>
                      <FontAwesomeIcon icon={faPrint} /> Print Quality PDF
                    </a>
                  </ArtworkDownload>

                  {!isUploadedArtwork && (
                    <ArtworkDownload artwork={artwork} key="web" queryParams={{ low_res: true }}>
                      {/*
                       // @ts-expect-error TS2339 */}
                      <a href={`${artwork.links.download_url}?low_res=true`}>
                        <FontAwesomeIcon icon={faGlobe} /> Web Quality PDF
                      </a>
                    </ArtworkDownload>
                  )}

                  {artworkImageDownloadable && (
                    <>
                      <ArtworkDownload
                        artwork={artwork}
                        key="JPG"
                        imageType="JPG"
                        // @ts-expect-error TS2339
                        queryParams={{ artwork_page_id: selectedArtworkPage.id, image_type: 'JPG' }}
                      >
                        {/*
                         // @ts-expect-error TS2339 */}
                        <a href={`${artwork.links?.export_url}?format=JPG`}>
                          <FontAwesomeIcon icon={faImage} /> JPG Image
                        </a>
                      </ArtworkDownload>

                      <ArtworkDownload
                        artwork={artwork}
                        key="PNG"
                        imageType="PNG"
                        // @ts-expect-error TS2339
                        queryParams={{ artwork_page_id: selectedArtworkPage.id, image_type: 'PNG' }}
                      >
                        {/*
                         // @ts-expect-error TS2339 */}
                        <a href={`${artwork.links.export_url}?format=PNG`}>
                          <FontAwesomeIcon icon={faImage} /> PNG Image
                        </a>
                      </ArtworkDownload>
                    </>
                  )}

                  {artworkImposable && (
                    <ArtworkDownload artwork={artwork} shouldImpose>
                      {/*
                       // @ts-expect-error TS2322 */}
                      <a tabIndex="0">
                        <FontAwesomeIcon icon={faClone} /> Imposed Artwork
                      </a>
                    </ArtworkDownload>
                  )}

                  {paginatedProofDownloadable && (
                    // @ts-expect-error TS2339
                    <a href={`/agency/publication_bookings/${publicationBooking.id}/pdf`}>
                      <FontAwesomeIcon icon={faNewspaper} /> Paginated Proof
                    </a>
                  )}
                </IconWithMenu.Menu>
              </IconWithMenu>

              {canChangeArtworkStatus && (
                <IconWithMenu
                  boxProps={{
                    flexGrow: 0.9,
                    width: '100%',
                  }}
                  icon={(
                    <Button
                      buttonStyle="secondaryUtility"
                      icon={<FontAwesomeIcon icon={faCog} />}
                      // @ts-expect-error TS2322
                      marginLeft="medium"
                      size="medium"
                      width="100%"
                    >
                      More
                    </Button>
                  )}
                >
                  {/*
                   // @ts-expect-error TS2741 */}
                  <IconWithMenu.Menu
                    closeOnClick
                    listStyle={{ right: '-7px' }}
                  >
                    {!artworkApproved && (
                      <EnabledPermission
                        campaignType={getCampaignType(campaign)}
                        permissionKey="campaign.artwork.approve"
                      >
                        <a
                          key="approve"
                          onClick={() => confirmAction({
                            callbacks: {
                              actionFn: approveFn,
                            },
                            strings: {
                              actionString: 'approve',
                              entityName: 'artwork',
                            },
                          })}
                          tabIndex={0}
                        >
                          <FontAwesomeIcon icon={faCheck} /> Approve Artwork
                        </a>
                      </EnabledPermission>
                    )}

                    {!artworkApproved && (
                      <a
                        key="delete"
                        onClick={() => confirmDelete({
                          callbacks: {
                            deleteFn,
                          },
                          strings: {
                            deleteString: 'delete',
                            entityName: 'artwork',
                          },
                        })}
                        tabIndex={0}
                      >
                        <FontAwesomeIcon icon={faTrashAlt} /> Delete Artwork
                      </a>
                    )}

                    {artworkUnapprovable && (
                      <EnabledPermission
                        campaignType={getCampaignType(campaign)}
                        permissionKey="campaign.artwork.approve"
                      >
                        <a
                          key="unapprove"
                          onClick={() => confirmDelete({
                            callbacks: {
                              deleteFn: unapproveFn,
                            },
                            strings: {
                              confirmString: isPublicationOrder
                                ? 'Are you sure? Please confirm with your master paginator.'
                                : 'Are you sure?',
                              deleteString: 'unapprove',
                              entityName: 'artwork',
                            },
                          })}
                          tabIndex={0}
                        >
                          <FontAwesomeIcon icon={faTimes} /> Unapprove Artwork
                        </a>
                      </EnabledPermission>
                    )}
                  </IconWithMenu.Menu>
                </IconWithMenu>
              )}
            </Box>
            {/* End Download & More Options */}
          </Box>
        )}
      </FormField>

      {/*
       // @ts-expect-error TS2339 */}
      {!!artwork.id && (
        <>
          <DataChanges artworkPayload={artworkPayload} />
          <Pages artworkPayload={artworkPayload} />
          <Reviews artworkPayload={artworkPayload} />
          <Activity artworkPayload={artworkPayload} />
        </>
      )}

      {/* {!!filteredArtworkReviews.length && ( TODO
        <Reviews allApproved={allArtworkReviewsApproved} artworkReviews={filteredArtworkReviews} />
      )} */}
    </>
  )
}

export default Artwork
